import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { MultiselectOption } from '@cbo/shared-library';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Multiselect from '../GlobalFilterBar/Multiselect/Multiselect';
import { useUsers } from '../../contexts/userContext';
import { GlAccountsSidebarState } from './GeneralLedgerAccountsSidebar';
import GeneralLedgerAccountsDataGrid from './GeneralLedgerAccountsDataGrid';
import { getManageReportOuterBoxSx, manageReportFiltersGrid } from './styles';
import SalesUtilities from '../../../sales/utilities';
import useYupValidationResolver from '../../utils/formUtils/yupValidationResolver';
import { getOptionLabels, mapSchemaValues } from '../../utils/reportingUtils/filterUtils';
import { useGetGlAccountFilters, useGlAccountsQuery } from '../../../api/sales/queries';
import { GlAccount } from '../../../api/sales/requests';

// Enum for filter options for active/inactive GL accounts
export enum GlAccountsFilterIsActive {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

// Interface for the filter state
export interface GlAccountsFilters {
  isActive: MultiselectOption[];
  glCategory: MultiselectOption[];
  glType: MultiselectOption[];
  glDetailType: MultiselectOption[];
}

interface GlAccountRow {
  [key: string]: unknown;
  hierarchy: string[];
  id: string;
}

export type GlAccountRows = GlAccount & GlAccountRow;

export type GeneralLedgerAccountsManageReportProps = {
  isGlAccountRowOpen: boolean;
  setIsGlAccountRowOpen: Dispatch<SetStateAction<boolean>>;
  sidebarState: GlAccountsSidebarState;
  setSidebarState: Dispatch<SetStateAction<GlAccountsSidebarState>>;
};

/**
 * Main component for managing General Ledger Accounts report.
 * Handles filter state, data fetching, and rendering the data grid.
 */
function GeneralLedgerAccountsManageReport(props: GeneralLedgerAccountsManageReportProps) {
  const { isGlAccountRowOpen, setIsGlAccountRowOpen, sidebarState, setSidebarState } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery((themes: Theme) => themes.breakpoints.down('sm'));

  const user = useUsers();
  const prevFiltersRef = useRef<GlAccountsFilters>();

  // Set up react-hook-form for filter controls
  const formContext = useForm({
    mode: 'onChange',
    resolver: useYupValidationResolver(
      yup.object(mapSchemaValues(['isActive', 'glCategory', 'glType', 'glDetailType']))
    ),
    defaultValues: {
      isActive: [],
      glCategory: [],
      glType: [],
      glDetailType: [],
    },
  });

  const { glAccounts, setGlAccountsContext, isError, isLoading } = useGlAccountsQuery(user.org?.bslId);
  const { glAccountFilters, isGetGlAccountFiltersDataLoading: filtersLoading } = useGetGlAccountFilters();

  // Update request filters when filter values change.
  useEffect(() => {
    const subscription = formContext.watch((filterValues: GlAccountsFilters) => {
      if (isEqual(prevFiltersRef.current, filterValues)) return;
      const { isActive, glCategory, glType, glDetailType } = filterValues;
      setGlAccountsContext({
        isActive: SalesUtilities.updateGlAccountsIsActiveFilter(isActive.map((r) => r.label)),
        categoryIds: glCategory.map((cat) => parseInt(cat.id ?? '', 10)),
        accountTypes: getOptionLabels(glType),
        accountDetailTypes: getOptionLabels(glDetailType),
      });
      prevFiltersRef.current = filterValues;
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext.watch]);

  // Define filter controls for the UI and only update when dependencies change
  const filters = useMemo(
    () => [
      {
        key: 'gl-active-multiselect',
        ariaLabel: t('admin.glAccountConfiguration.generalLedgerAccounts.isActiveFilter'),
        controllerName: 'isActive',
        singular: t('admin.glAccountConfiguration.generalLedgerAccounts.isActive'),
        plural: t('admin.glAccountConfiguration.generalLedgerAccounts.statuses'),
        options: [
          { label: GlAccountsFilterIsActive.ACTIVE.toString() },
          { label: GlAccountsFilterIsActive.INACTIVE.toString() },
        ],
      },
      {
        key: 'gl-category-multiselect',
        ariaLabel: t('admin.glAccountConfiguration.generalLedgerAccounts.glCategoryFilter'),
        controllerName: 'glCategory',
        singular: t('admin.glAccountConfiguration.generalLedgerAccounts.glCategory'),
        plural: t('admin.glAccountConfiguration.generalLedgerAccounts.glCategories'),
        options: glAccountFilters?.categoryInfo.map((category) => ({
          label: category.categoryName,
          id: category.categoryId.toString(),
        })),
      },
      {
        key: 'gl-type-multiselect',
        ariaLabel: t('admin.glAccountConfiguration.generalLedgerAccounts.glTypeFilter'),
        controllerName: 'glType',
        singular: t('admin.glAccountConfiguration.generalLedgerAccounts.glType'),
        plural: t('admin.glAccountConfiguration.generalLedgerAccounts.glTypes'),
        options: glAccountFilters?.accountTypes.map((e) => ({
          label: e,
        })),
      },
      {
        key: 'gl-detail-multiselect',
        ariaLabel: t('admin.glAccountConfiguration.generalLedgerAccounts.glDetailTypeFilter'),
        controllerName: 'glDetailType',
        singular: t('admin.glAccountConfiguration.generalLedgerAccounts.glDetailType'),
        plural: t('admin.glAccountConfiguration.generalLedgerAccounts.glDetailTypes'),
        options: glAccountFilters?.accountDetailTypes.map((e) => ({
          label: e,
        })),
      },
    ],
    [glAccountFilters, t]
  );

  return (
    <Box sx={getManageReportOuterBoxSx(isGlAccountRowOpen && !isMobile)}>
      <Grid item xs={6}>
        {t('admin.glAccountConfiguration.generalLedgerAccounts.manageIntro')}
      </Grid>
      <FormContainer formContext={formContext}>
        <Grid container sx={manageReportFiltersGrid} spacing='8px'>
          {filters.map((filter) => (
            <Grid item key={filter.key}>
              <Multiselect
                plural={filter.plural}
                singular={filter.singular}
                controllerName={filter.controllerName}
                options={filter.options ?? []}
                defaultChecked={false}
                loading={filtersLoading}
              />
            </Grid>
          ))}
        </Grid>
      </FormContainer>
      <GeneralLedgerAccountsDataGrid
        isGlAccountRowOpen={isGlAccountRowOpen}
        setIsGlAccountRowOpen={setIsGlAccountRowOpen}
        sidebarState={sidebarState}
        setSidebarState={setSidebarState}
        glAccounts={glAccounts}
        isError={isError}
        isLoading={isLoading}
      />
    </Box>
  );
}

export default GeneralLedgerAccountsManageReport;
